import { supabase } from '../lib/supabase'

import type {

  AppointmentContactIds,

  EnsureAppointmentContactsInput,

  EnsurePeopleContactInput,

  PeopleContact,

  PeopleContactOwner,

  PeopleContactType,

} from '../types/peopleContacts'



function onlyDigits(value: string): string {

  return value.replace(/\D/g, '')

}



function normalizeEmail(value: string): string {

  return value.trim().toLowerCase()

}



export function normalizePeopleContactValue(

  contactType: PeopleContactType,

  value: string

): string {

  if (contactType === 'phone') return onlyDigits(value)

  return normalizeEmail(value)

}



export function resolveContactPersonTaxId(params: {

  holderCpf: string

  patientCpf: string

  patientIsInsured: boolean

  contactOwner: PeopleContactOwner

}): string {

  const holder = onlyDigits(params.holderCpf)

  const patient = params.patientIsInsured ? holder : onlyDigits(params.patientCpf)

  if (!holder || holder.length !== 11) {

    throw new Error('CPF do segurado inválido para gravar contato.')

  }

  if (holder === patient) return holder

  if (params.contactOwner === 'patient') {

    if (!patient || patient.length !== 11) {

      throw new Error('CPF do paciente inválido para gravar contato.')

    }

    return patient

  }

  return holder

}



function mapRow(row: Record<string, unknown>): PeopleContact {

  const contactType: PeopleContactType =

    row.contact_type === 'email' ? 'email' : 'phone'

  return {

    id: String(row.id),

    person_tax_id: String(row.person_tax_id ?? ''),

    contact_type: contactType,

    value: String(row.value ?? ''),

    label: (row.label as string | null) ?? null,

    created_at: String(row.created_at ?? ''),

    created_by: (row.created_by as string | null) ?? null,

    updated_at: String(row.updated_at ?? ''),

    updated_by: (row.updated_by as string | null) ?? null,

    deleted_at: (row.deleted_at as string | null) ?? null,

    deleted_by: (row.deleted_by as string | null) ?? null,

  }

}



export const peopleContactsService = {

  async listByPerson(

    taxId: string,

    options?: { includeDeleted?: boolean }

  ): Promise<PeopleContact[]> {

    const personTaxId = onlyDigits(taxId)

    if (!personTaxId) return []



    let query = supabase

      .from('people_contacts')

      .select('*')

      .eq('person_tax_id', personTaxId)

      .order('created_at', { ascending: false })



    if (!options?.includeDeleted) {

      query = query.is('deleted_at', null)

    }



    const { data, error } = await query

    if (error) throw error

    return (data ?? []).map((row) => mapRow(row as Record<string, unknown>))

  },



  async getByIds(ids: string[]): Promise<Map<string, PeopleContact>> {

    const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))]

    const map = new Map<string, PeopleContact>()

    if (unique.length === 0) return map



    const { data, error } = await supabase

      .from('people_contacts')

      .select('*')

      .in('id', unique)



    if (error) throw error

    for (const row of data ?? []) {

      const contact = mapRow(row as Record<string, unknown>)

      map.set(contact.id, contact)

    }

    return map

  },



  /** CPFs com telefone ativo cujo valor (só dígitos) contém o trecho buscado. */

  async findTaxIdsMatchingPhone(phoneFragment: string): Promise<string[]> {

    const needle = onlyDigits(phoneFragment)

    if (!needle) return []



    const { data, error } = await supabase

      .from('people_contacts')

      .select('person_tax_id, value')

      .eq('contact_type', 'phone')

      .is('deleted_at', null)



    if (error) throw error



    const ids = new Set<string>()

    for (const row of data ?? []) {

      const taxId = onlyDigits(String((row as { person_tax_id?: string }).person_tax_id ?? ''))

      const value = onlyDigits(String((row as { value?: string }).value ?? ''))

      if (taxId && value.includes(needle)) ids.add(taxId)

    }

    return [...ids]

  },



  async ensureContact(input: EnsurePeopleContactInput): Promise<PeopleContact> {

    const personTaxId = onlyDigits(input.personTaxId)

    if (personTaxId.length !== 11) {

      throw new Error('CPF inválido para gravar contato.')

    }



    const value = normalizePeopleContactValue(input.contactType, input.value)

    if (!value) throw new Error('Informe o valor do contato.')

    if (input.contactType === 'phone' && value.length < 10) {

      throw new Error('Telefone inválido para gravar contato.')

    }

    if (input.contactType === 'email' && !value.includes('@')) {

      throw new Error('E-mail inválido para gravar contato.')

    }



    const existing = await this.listByPerson(personTaxId)

    const found = existing.find(

      (row) =>

        row.contact_type === input.contactType &&

        normalizePeopleContactValue(row.contact_type, row.value) === value

    )

    if (found) return found



    const userId = input.userId?.trim() || null

    const payload = {

      person_tax_id: personTaxId,

      contact_type: input.contactType,

      value,

      label: input.label?.trim() || null,

      created_by: userId,

      updated_by: userId,

    }



    const { data, error } = await supabase

      .from('people_contacts')

      .insert(payload)

      .select('*')

      .single()



    if (error) throw error

    return mapRow(data as Record<string, unknown>)

  },



  async softDelete(id: string, userId: string): Promise<void> {

    if (!id.trim()) throw new Error('Contato inválido.')

    if (!userId.trim()) throw new Error('Usuário responsável pela exclusão não identificado.')



    const { error } = await supabase

      .from('people_contacts')

      .update({

        deleted_at: new Date().toISOString(),

        deleted_by: userId.trim(),

        updated_by: userId.trim(),

      })

      .eq('id', id)

      .is('deleted_at', null)



    if (error) throw error

  },



  /** Garante telefone/e-mails em people_contacts e retorna IDs para o agendamento. */

  async ensureForAppointment(

    input: EnsureAppointmentContactsInput

  ): Promise<AppointmentContactIds> {

    const personTaxId = resolveContactPersonTaxId({

      holderCpf: input.holderCpf,

      patientCpf: input.patientCpf,

      patientIsInsured: input.patientIsInsured,

      contactOwner: input.contactOwner,

    })

    const userId = input.userId?.trim() || null

    const label = input.label?.trim() || 'agenda'



    let phoneContactId: string | null = null

    const phone = input.phone?.trim()

    if (phone) {

      const contact = await this.ensureContact({

        personTaxId,

        contactType: 'phone',

        value: phone,

        label,

        userId,

      })

      phoneContactId = contact.id

    }



    const emailContactIds: string[] = []

    for (const raw of input.emails) {

      const email = normalizeEmail(raw)

      if (!email) continue

      const contact = await this.ensureContact({

        personTaxId,

        contactType: 'email',

        value: email,

        label,

        userId,

      })

      if (!emailContactIds.includes(contact.id)) {

        emailContactIds.push(contact.id)

      }

    }



    return { phoneContactId, emailContactIds }

  },

}


